import { useState } from "react";
import { BsEye, BsEyeSlash } from "react-icons/bs";

import LoadingButton from "@/components/loading-button";
import { useComponentTranslation } from "@/services/i18n";
import LinkedInField from "./fields/LinkedInField";
import PortfolioField from "./fields/PortfolioField";
import PhoneField from "./fields/PhoneField";
import CoverLetterField from "./fields/CoverLetterField";
import AddressField from "./fields/AddressField";
import CityField from "./fields/CityField";
import CountryField from "./fields/CountryField";
import AvailabilityField from "./fields/AvailabilityField";
import SalaryExpectationsField from "./fields/SalaryExpectationsField";
import ResumeField from "./fields/ResumeField";
import CustomQuestionsField from "./fields/CustomQuestionsField";

const translations = {
  en: {
    auth: {
      register: {
        firstname: "First name",
        lastname: "Last name",
        email: "Email",
        resume: "Resume",
        resume_upload: "Upload resume",
        resume_only_pdf: "only PDF",
        profile_retention: "Would you like us to keep your profile for upcoming job openings or potential projects?",
        firstname_placeholder: "Enter your first name",
        lastname_placeholder: "Enter your last name",
        email_placeholder: "example@mail.com",
        button: "Apply",
        missing_fields: "Please fill in all mandatory fields",
      },
      sign_in: {
        password: "Password",
      },
    },
  },
  fr: {
    auth: {
      register: {
        firstname: "Prénom",
        lastname: "Nom",
        email: "Email",
        resume: "CV",
        resume_upload: "Télécharger le CV",
        resume_only_pdf: "PDF uniquement",
        profile_retention: "Souhaitez-vous que nous conservions votre profil pour les prochaines offres d'emploi ou projets potentiels ?",
        firstname_placeholder: "Entrez votre prénom",
        lastname_placeholder: "Entrez votre nom",
        email_placeholder: "exemple@mail.com",
        button: "Postuler",
        missing_fields: "Veuillez remplir tous les champs obligatoires",
      },
      sign_in: {
        password: "Mot de passe",
      },
    },
  },
};

export default function SignupForm({ user, onSubmit, loading, job }) {
  const { t } = useComponentTranslation(translations);

  const visibleFields = job?.application_form_fields?.filter((field) => field.visible === true) || [];

  const [values, setValues] = useState(user || { firstname: "", lastname: "", email: "", password: "" });
  const [seePassword, setSeePassword] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Get list of missing mandatory fields
  const getMissingMandatoryFields = () => {
    const missing = [];

    // Check firstname, lastname, email
    if (!values.firstname || values.firstname.trim() === "") {
      missing.push("firstname");
    }
    if (!values.lastname || values.lastname.trim() === "") {
      missing.push("lastname");
    }
    if (!values.email || values.email.trim() === "") {
      missing.push("email");
    }

    // Check password
    if (!values.password) {
      missing.push("password");
    }

    // Check all visible mandatory fields
    const mandatoryFields = visibleFields.filter((field) => field.mandatory === true);
    for (const field of mandatoryFields) {
      // Skip fullname - we already require firstname and lastname separately
      if (field.id === "fullname") continue;

      const value = values[field.id];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        missing.push(field.id);
      }
    }

    // Check required custom questions
    const requiredQuestions = (job?.custom_questions || []).filter((q) => q.required);
    for (const question of requiredQuestions) {
      const value = values[`custom_${question.id}`];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        missing.push(`custom_${question.id}`);
      }
    }

    return missing;
  };

  // Check if all mandatory fields are filled
  const isFormValid = () => {
    return getMissingMandatoryFields().length === 0;
  };

  // Prepare form data for submission - include file data
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!isFormValid()) {
      return;
    }

    // Values already contain base64 file data for resume fields
    onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-y-6">
      {/* Always show firstname, lastname, and email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstname" className="input-label">
            {t("auth.register.firstname")} *
          </label>
          <input
            type="text"
            id="firstname"
            name="firstname"
            value={values.firstname || ""}
            onChange={(e) => setValues({ ...values, firstname: e.target.value })}
            className={`input ${showValidation && (!values.firstname || values.firstname.trim() === "") ? "border-red-500" : ""}`}
            placeholder={t("auth.register.firstname_placeholder")}
          />
        </div>
        <div>
          <label htmlFor="lastname" className="input-label">
            {t("auth.register.lastname")} *
          </label>
          <input
            type="text"
            id="lastname"
            name="lastname"
            value={values.lastname || ""}
            onChange={(e) => setValues({ ...values, lastname: e.target.value })}
            className={`input ${showValidation && (!values.lastname || values.lastname.trim() === "") ? "border-red-500" : ""}`}
            placeholder={t("auth.register.lastname_placeholder")}
          />
        </div>
      </div>
      <div>
        <label htmlFor="email" className="input-label">
          {t("auth.register.email")} *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={values.email || ""}
          onChange={(e) => setValues({ ...values, email: e.target.value })}
          className={`input ${showValidation && (!values.email || values.email.trim() === "") ? "border-red-500" : ""}`}
          placeholder={t("auth.register.email_placeholder")}
        />
      </div>

      {/* Dynamic fields from application_form_fields */}
      <ResumeField visibleFields={visibleFields} value={values.resume} setValue={(val) => setValues({ ...values, resume: val })} showValidation={showValidation} />
      <LinkedInField visibleFields={visibleFields} value={values.linkedin} onChange={(e) => setValues({ ...values, linkedin: e.target.value })} showValidation={showValidation} />
      <PortfolioField
        visibleFields={visibleFields}
        value={values.portfolio}
        onChange={(e) => setValues({ ...values, portfolio: e.target.value })}
        showValidation={showValidation}
      />
      <PhoneField visibleFields={visibleFields} value={values.phone} onChange={(e) => setValues({ ...values, phone: e.target.value })} showValidation={showValidation} />
      <CoverLetterField
        visibleFields={visibleFields}
        value={values.cover_letter}
        onChange={(e) => setValues({ ...values, cover_letter: e.target.value })}
        showValidation={showValidation}
      />
      <AddressField visibleFields={visibleFields} value={values.address} onChange={(e) => setValues({ ...values, address: e.target.value })} showValidation={showValidation} />
      <CityField visibleFields={visibleFields} value={values.city} onChange={(e) => setValues({ ...values, city: e.target.value })} showValidation={showValidation} />
      <CountryField visibleFields={visibleFields} value={values.country} onChange={(e) => setValues({ ...values, country: e.target.value })} showValidation={showValidation} />
      <AvailabilityField
        visibleFields={visibleFields}
        value={values.availability}
        onChange={(e) => setValues({ ...values, availability: e.target.value })}
        showValidation={showValidation}
      />
      <SalaryExpectationsField
        visibleFields={visibleFields}
        value={values.salary_expectations}
        onChange={(e) => setValues({ ...values, salary_expectations: e.target.value })}
        showValidation={showValidation}
      />

      {/* Custom Questions */}
      <CustomQuestionsField
        questions={job?.custom_questions}
        values={values}
        setValues={setValues}
        showValidation={showValidation}
      />

      {/* Password - always required for authentication */}
      <div>
        <label htmlFor="password" className="input-label">
          {t("auth.sign_in.password")} *
        </label>
        <div className="relative">
          <input
            type={seePassword ? "text" : "password"}
            id="password"
            name="password"
            value={values.password}
            onChange={(e) => setValues({ ...values, password: e.target.value })}
            className="input pr-10"
            placeholder="••••••••"
            required
          />
          <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600" onClick={() => setSeePassword(!seePassword)}>
            {seePassword ? <BsEyeSlash className="h-5 w-5" /> : <BsEye className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Validation warning */}
      {showValidation && !isFormValid() && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{t("auth.register.missing_fields")}</div>}

      {/* Submit Button */}
      <LoadingButton loading={loading} className="btn btn-primary w-full btn-large">
        {t("auth.register.button")}
      </LoadingButton>
    </form>
  );
}
